import { createSlice } from '@reduxjs/toolkit';

import { GeneralStepConversationTopics, TicketStages } from 'core/enums/common';

import {
	createTicket,
	deleteTicket,
	getChangesHistory,
	getTicketsByUserId,
	searchTickets,
	updateTicket,
} from './asyncActions';
import { ITicketReducer, TicketData } from './types';

const initialState: ITicketReducer = {
	loading: false,
	loadingMore: false,
	updating: false,
	tickets: [],
	activeTicket: null,
	isTicketUpdate: false,
	isTyping: false,
	voiceMode: false,
	isRecording: false,
	isSocketConnected: false,
	isStreaming: false,
	isSummaryLoading: false,
	isUsecasesLoading: false,
	isUseCasesGenerating: false,
	isTestCasesLoading: false,
	isTestCasesGenerating: false,
	isNotificationLoading: false,
	isReportLoading: false,
	activeStep: TicketStages.general,
	activeSubStage: GeneralStepConversationTopics['Functional requirements'],
	detailedViewTicketSummary: null,
	reportAnIssueMode: false,
	paymentMode: false,
	paymentModalInfo: {
		isOpen: false,
		paymentMethods: [],
		previousUrlQuery: 'isProfile=false',
	},
	isTariffPlansModalOpen: false,
	editMode: false,
	ticketChanges: [],
	activeHistoryItem: null,
	isHistoryLoading: false,
	historyMode: false,
	sidebarView: true,
	isChatOpen: true,
	isStepperStagesOpen: false,
	isStepperSubStagesOpen: false,
};

const ticketSlice = createSlice({
	name: 'ticket',
	initialState,
	reducers: {
		setActiveTicket: (state, action) => {
			state.activeTicket = action.payload;
		},
		addLocalTicket: (state, action) => {
			state.tickets.unshift(action.payload);
		},
		removeLocalTicket: (state) => {
			state.tickets.shift();
		},
		resetTickets: (state) => {
			state.tickets = initialState.tickets;
			state.activeTicket = initialState.activeTicket;
			state.reportAnIssueMode = false;
			state.editMode = false;
		},
		updateLocalTicket: (state, action) => {
			const ticketIndex = state.tickets.findIndex((ticket) => ticket.id === action.payload.id);
			if (ticketIndex !== -1) {
				const existingTicket = state.tickets[ticketIndex];
				const updatedTicket = { ...existingTicket, ...action.payload };

				state.tickets.splice(ticketIndex, 1, updatedTicket);
			}
		},
		setIsTicketUpdate: (state, action) => {
			state.isTicketUpdate = action.payload;
		},
		setTickets: (state, action) => {
			const data = action.payload;

			state.tickets = data.sort((a: TicketData, b: TicketData) => {
				const dateA = a.updatedDate || a.createdDate;
				const dateB = b.updatedDate || b.createdDate;
				const timestampA = new Date(dateA).getTime();
				const timestampB = new Date(dateB).getTime();
				return timestampB - timestampA;
			});
		},
		appendTickets: (state, action) => {
			state.tickets.push(...action.payload);
		},
		setIsTyping: (state, action) => {
			if (state.activeTicket) state.isTyping = action.payload;
		},
		setVoiceMode: (state, action) => {
			if (state.activeTicket) state.voiceMode = action.payload;
		},
		setIsRecording: (state, action) => {
			if (state.activeTicket) state.isRecording = action.payload;
		},
		setIsSocketConnected: (state, action) => {
			if (state.activeTicket) state.isSocketConnected = action.payload;
		},
		setIsStreaming: (state, action) => {
			if (state.activeTicket) state.isStreaming = action.payload;
		},
		setIsSummaryLoading: (state, action) => {
			if (state.activeTicket) state.isSummaryLoading = action.payload;
		},
		setIsUsecasesLoading: (state, action) => {
			if (state.activeTicket) state.isUsecasesLoading = action.payload;
		},
		setIsTestCasesLoading: (state, action) => {
			if (state.activeTicket) state.isTestCasesLoading = action.payload;
		},
		setIsNotificationLoading: (state, action) => {
			if (state.activeTicket) state.isNotificationLoading = action.payload;
		},
		setIsReportLoading: (state, action) => {
			if (state.activeTicket) state.isReportLoading = action.payload;
		},
		// setNeedLoadUseCases: (state, action) => {
		// 	if (state.activeTicket) state.needLoadUseCases = action.payload;
		// },
		setActiveStep: (state, action) => {
			state.activeStep = action.payload;
		},
		setActiveSubStage: (state, action) => {
			state.activeSubStage = action.payload;
		},
		setDetailedViewTicketSummary: (state, action) => {
			state.detailedViewTicketSummary = action.payload;
		},
		setReportAnIssueMode: (state, action) => {
			state.reportAnIssueMode = action.payload;
		},
		setPaymentMode: (state, action) => {
			state.paymentMode = action.payload;
		},
		setUpdatePaymentInfoModal: (state, action) => {
			state.paymentModalInfo = action.payload;
		},
		setIsTariffPlansModalOpen: (state, action) => {
			state.isTariffPlansModalOpen = action.payload;
		},
		setEditMode: (state, action) => {
			state.editMode = action.payload;
		},
		setTicketHistory: (state, action) => {
			state.ticketChanges = action.payload;
		},
		setActiveHistoryItem: (state, action) => {
			state.activeHistoryItem = action.payload;
		},
		setHistoryMode: (state, action) => {
			state.historyMode = action.payload;
		},
		setSidebarView: (state, action) => {
			state.sidebarView = action.payload;
		},
		setIsUsecasesGenerating: (state, { payload }) => {
			state.isUseCasesGenerating = payload;
		},
		setIsTestCasesGenerating: (state, { payload }) => {
			state.isTestCasesGenerating = payload;
		},
		setIsChatOpen: (state, action) => {
			state.isChatOpen = action.payload;
		},
		setIsStepperStagesOpen: (state, action) => {
			state.isStepperStagesOpen = action.payload;
		},
		setIsStepperSubStagesOpen: (state, action) => {
			state.isStepperSubStagesOpen = action.payload;
		},
		resetTicketsStore: () => initialState,
	},
	extraReducers: (builder) => {
		builder
			.addCase(getTicketsByUserId.pending, (state) => {
				state.loadingMore = true;
			})
			.addCase(getTicketsByUserId.fulfilled, (state) => {
				state.loadingMore = false;
			})
			.addCase(searchTickets.pending, (state) => {
				state.loadingMore = true;
			})
			.addCase(searchTickets.fulfilled, (state) => {
				state.loadingMore = false;
			})
			.addCase(createTicket.pending, (state) => {
				state.loading = true;
			})
			.addCase(createTicket.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(createTicket.rejected, (state) => {
				state.loading = false;
			})
			.addCase(updateTicket.pending, (state) => {
				state.updating = true;
			})
			.addCase(updateTicket.fulfilled, (state) => {
				state.updating = false;
			})
			.addCase(updateTicket.rejected, (state) => {
				state.updating = false;
			})
			.addCase(deleteTicket.pending, (state) => {
				state.updating = true;
			})
			.addCase(deleteTicket.fulfilled, (state) => {
				state.updating = false;
			})
			.addCase(deleteTicket.rejected, (state) => {
				state.updating = false;
			})
			.addCase(getChangesHistory.pending, (state) => {
				state.isHistoryLoading = true;
				state.ticketChanges = [];
			})
			.addCase(getChangesHistory.fulfilled, (state) => {
				state.isHistoryLoading = false;
			})
			.addCase(getChangesHistory.rejected, (state) => {
				state.isHistoryLoading = false;
				state.ticketChanges = [];
			});
	},
});

export const {
	setActiveTicket,
	setTickets,
	appendTickets,
	addLocalTicket,
	resetTickets,
	removeLocalTicket,
	updateLocalTicket,
	setIsTicketUpdate,
	setIsTyping,
	setVoiceMode,
	setIsRecording,
	setIsStreaming,
	setIsSocketConnected,
	setIsSummaryLoading,
	setIsUsecasesLoading,
	setIsTestCasesLoading,
	setIsNotificationLoading,
	setIsReportLoading,
	setActiveStep,
	setActiveSubStage,
	setDetailedViewTicketSummary,
	setReportAnIssueMode,
	setPaymentMode,
	setUpdatePaymentInfoModal,
	setIsTariffPlansModalOpen,
	setEditMode,
	setTicketHistory,
	setActiveHistoryItem,
	setHistoryMode,
	setSidebarView,
	setIsStepperStagesOpen,
	setIsStepperSubStagesOpen,
	setIsUsecasesGenerating,
	setIsTestCasesGenerating,
	resetTicketsStore,
	setIsChatOpen,
} = ticketSlice.actions;

export default ticketSlice.reducer;
